import { useState, useEffect } from 'react';
import { Mail, Users, Calendar, MapPin, Clock, Send, Filter, Download, Trash2, Plus, LogOut } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { IslamicEventsPanel } from './IslamicEventsPanel';
import { EmailTemplates } from './EmailTemplates';
import { useAuth } from '../contexts/AuthContext';
import { loadLocalPrayerTimes, convertToPrayerDisplays } from '../utils/prayerTimes';
import { PrayerDisplay } from '../types/prayer';

interface Subscriber {
  id: string;
  email: string;
  name: string;
  location: string;
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  timezone: string;
  is_active: boolean;
  notify_20min: boolean;
  notify_10min: boolean;
  notify_exact: boolean;
  created_at: string;
}

interface PrayerTimes {
  fajr: string;
  sunrise: string;
  dhuhr: string;
  asr: string;
  maghrib: string;
  isha: string;
}

export function CRMPage() {
  const { user, signOut } = useAuth();
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [selectedSubscribers, setSelectedSubscribers] = useState<Set<string>>(new Set());
  const [showEmailPanel, setShowEmailPanel] = useState(false);
  const [locations, setLocations] = useState<string[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [prayers, setPrayers] = useState<PrayerDisplay[]>([]);
  const [newSubscriber, setNewSubscriber] = useState({
    email: '',
    name: '',
    city: '',
    country: '',
    timezone: 'America/Toronto'
  });

  useEffect(() => {
    fetchSubscribers();
    loadPrayerTimes();
  }, []);

  const loadPrayerTimes = async () => {
    try {
      const localTimes = await loadLocalPrayerTimes(new Date());
      if (localTimes) {
        const displays = convertToPrayerDisplays(localTimes);
        setPrayers(displays);
      }
    } catch (error) {
      console.error('Error loading prayer times:', error);
    }
  };

  const fetchSubscribers = async () => {
    try {
      const { data, error } = await supabase
        .from('email_subscriptions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setSubscribers(data || []);

      const uniqueLocations = [...new Set(data?.map(s => `${s.city}, ${s.country}`) || [])];
      setLocations(uniqueLocations);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSubscribers = selectedLocation === 'all'
    ? subscribers
    : subscribers.filter(s => `${s.city}, ${s.country}` === selectedLocation);

  const toggleSubscriber = (id: string) => {
    const newSelected = new Set(selectedSubscribers);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedSubscribers(newSelected);
  };

  const selectAll = () => {
    if (selectedSubscribers.size === filteredSubscribers.length) {
      setSelectedSubscribers(new Set());
    } else {
      setSelectedSubscribers(new Set(filteredSubscribers.map(s => s.id)));
    }
  };

  const getNotificationSettings = (sub: Subscriber) => {
    const settings = [];
    if (sub.notify_20min) settings.push('20min');
    if (sub.notify_10min) settings.push('10min');
    if (sub.notify_exact) settings.push('Exact');
    return settings.join(', ');
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Location', 'City', 'Country', 'Timezone', 'Status', 'Notifications', 'Joined Date'];
    const rows = filteredSubscribers.map(sub => [
      sub.name,
      sub.email,
      sub.location,
      sub.city,
      sub.country,
      sub.timezone,
      sub.is_active ? 'Active' : 'Inactive',
      getNotificationSettings(sub),
      new Date(sub.created_at).toLocaleDateString()
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `subscribers-${selectedLocation}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const handleAddSubscriber = async () => {
    try {
      const { data, error } = await supabase
        .from('email_subscriptions')
        .insert([{
          email: newSubscriber.email,
          name: newSubscriber.name,
          city: newSubscriber.city,
          country: newSubscriber.country,
          location: `${newSubscriber.city}, ${newSubscriber.country}`,
          timezone: newSubscriber.timezone,
          is_active: true,
          notify_20min: true,
          notify_10min: true,
          notify_exact: true,
          latitude: '0',
          longitude: '0'
        }])
        .select();

      if (error) throw error;

      alert('Subscriber added successfully!');
      setShowAddForm(false);
      setNewSubscriber({ email: '', name: '', city: '', country: '', timezone: 'America/Toronto' });
      fetchSubscribers();
    } catch (error) {
      console.error('Error adding subscriber:', error);
      alert('Failed to add subscriber. Please try again.');
    }
  };

  const handleDeleteSubscriber = async (id: string, email: string) => {
    if (!confirm(`Are you sure you want to delete ${email}?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('email_subscriptions')
        .delete()
        .eq('id', id);

      if (error) throw error;

      alert('Subscriber deleted successfully!');
      fetchSubscribers();
      setSelectedSubscribers(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    } catch (error) {
      console.error('Error deleting subscriber:', error);
      alert('Failed to delete subscriber. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-800 via-teal-700 to-teal-900 flex items-center justify-center">
        <div className="text-white text-2xl">Loading CRM...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-800 via-teal-700 to-teal-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Prayer Times CRM</h1>
              <p className="text-white/80">Manage subscribers and send notifications</p>
              <p className="text-white/60 text-sm mt-1">Logged in as: {user?.email}</p>
            </div>
            <div className="flex gap-4 items-center">
              <div className="bg-white/20 rounded-xl p-4 text-center">
                <Users className="w-8 h-8 text-white mx-auto mb-2" />
                <div className="text-3xl font-bold text-white">{subscribers.length}</div>
                <div className="text-white/80 text-sm">Total Subscribers</div>
              </div>
              <div className="bg-white/20 rounded-xl p-4 text-center">
                <MapPin className="w-8 h-8 text-white mx-auto mb-2" />
                <div className="text-3xl font-bold text-white">{locations.length}</div>
                <div className="text-white/80 text-sm">Locations</div>
              </div>
              <button
                onClick={() => signOut()}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-all flex items-center gap-2 font-semibold"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Islamic Events Panel */}
        <IslamicEventsPanel prayers={prayers} />

        {/* Add New Subscriber */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-white">Subscriber Management</h3>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-all flex items-center gap-2 font-semibold"
            >
              <Plus className="w-4 h-4" />
              {showAddForm ? 'Cancel' : 'Add New Subscriber'}
            </button>
          </div>

          {showAddForm && (
            <div className="bg-white/5 rounded-xl p-6 border border-white/20">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-white/80 mb-2 text-sm">Email</label>
                  <input
                    type="email"
                    value={newSubscriber.email}
                    onChange={(e) => setNewSubscriber({...newSubscriber, email: e.target.value})}
                    placeholder="email@example.com"
                    className="w-full bg-white/10 text-white rounded-lg px-4 py-2 border border-white/30 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-white/80 mb-2 text-sm">Name</label>
                  <input
                    type="text"
                    value={newSubscriber.name}
                    onChange={(e) => setNewSubscriber({...newSubscriber, name: e.target.value})}
                    placeholder="Full Name"
                    className="w-full bg-white/10 text-white rounded-lg px-4 py-2 border border-white/30 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-white/80 mb-2 text-sm">City</label>
                  <input
                    type="text"
                    value={newSubscriber.city}
                    onChange={(e) => setNewSubscriber({...newSubscriber, city: e.target.value})}
                    placeholder="Windsor"
                    className="w-full bg-white/10 text-white rounded-lg px-4 py-2 border border-white/30 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-white/80 mb-2 text-sm">Country</label>
                  <input
                    type="text"
                    value={newSubscriber.country}
                    onChange={(e) => setNewSubscriber({...newSubscriber, country: e.target.value})}
                    placeholder="Canada"
                    className="w-full bg-white/10 text-white rounded-lg px-4 py-2 border border-white/30 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-white/80 mb-2 text-sm">Timezone</label>
                  <select
                    value={newSubscriber.timezone}
                    onChange={(e) => setNewSubscriber({...newSubscriber, timezone: e.target.value})}
                    className="w-full bg-white/10 text-white rounded-lg px-4 py-2 border border-white/30 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="America/Toronto">America/Toronto</option>
                    <option value="America/New_York">America/New_York</option>
                    <option value="America/Chicago">America/Chicago</option>
                    <option value="America/Denver">America/Denver</option>
                    <option value="America/Los_Angeles">America/Los_Angeles</option>
                    <option value="Europe/London">Europe/London</option>
                    <option value="Europe/Paris">Europe/Paris</option>
                    <option value="Asia/Dubai">Asia/Dubai</option>
                  </select>
                </div>
              </div>
              <button
                onClick={handleAddSubscriber}
                disabled={!newSubscriber.email || !newSubscriber.name || !newSubscriber.city || !newSubscriber.country}
                className="bg-amber-500 hover:bg-amber-600 disabled:bg-gray-500 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg transition-all font-semibold"
              >
                Add Subscriber
              </button>
            </div>
          )}
        </div>

        {/* Filters and Actions */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 flex-1">
              <Filter className="w-5 h-5 text-white" />
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="bg-white/20 text-white rounded-lg px-4 py-2 border border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 [&>option]:text-gray-900 [&>option]:bg-white"
              >
                <option value="all">All Locations ({subscribers.length})</option>
                {locations.map(loc => (
                  <option key={loc} value={loc}>
                    {loc} ({subscribers.filter(s => `${s.city}, ${s.country}` === loc).length})
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={selectAll}
              className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-all"
            >
              {selectedSubscribers.size === filteredSubscribers.length ? 'Deselect All' : 'Select All'}
            </button>

            <button
              onClick={exportToCSV}
              className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-all flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>

            <button
              onClick={() => setShowEmailPanel(true)}
              disabled={selectedSubscribers.size === 0}
              className="bg-amber-500 hover:bg-amber-600 disabled:bg-gray-500 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg transition-all flex items-center gap-2 font-semibold"
            >
              <Send className="w-4 h-4" />
              Send Email ({selectedSubscribers.size})
            </button>
          </div>
        </div>

        {/* Subscribers Table */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/20">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedSubscribers.size === filteredSubscribers.length && filteredSubscribers.length > 0}
                      onChange={selectAll}
                      className="w-4 h-4"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-white font-semibold">Name</th>
                  <th className="px-4 py-3 text-left text-white font-semibold">Email</th>
                  <th className="px-4 py-3 text-left text-white font-semibold">Location</th>
                  <th className="px-4 py-3 text-left text-white font-semibold">Timezone</th>
                  <th className="px-4 py-3 text-left text-white font-semibold">Status</th>
                  <th className="px-4 py-3 text-left text-white font-semibold">Notifications</th>
                  <th className="px-4 py-3 text-left text-white font-semibold">API Source</th>
                  <th className="px-4 py-3 text-left text-white font-semibold">Joined</th>
                  <th className="px-4 py-3 text-left text-white font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubscribers.map((sub) => (
                  <tr key={sub.id} className="border-t border-white/10 hover:bg-white/5">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedSubscribers.has(sub.id)}
                        onChange={() => toggleSubscriber(sub.id)}
                        className="w-4 h-4"
                      />
                    </td>
                    <td className="px-4 py-3 text-white">{sub.name}</td>
                    <td className="px-4 py-3 text-white">{sub.email}</td>
                    <td className="px-4 py-3 text-white">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {sub.city}, {sub.country}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white text-sm">{sub.timezone}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs ${sub.is_active ? 'bg-green-500' : 'bg-red-500'} text-white`}>
                        {sub.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white text-sm">{getNotificationSettings(sub)}</td>
                    <td className="px-4 py-3 text-white text-sm">
                      <span className="bg-blue-500/50 px-2 py-1 rounded text-xs">Local JSON</span>
                    </td>
                    <td className="px-4 py-3 text-white text-sm">
                      {new Date(sub.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleDeleteSubscriber(sub.id, sub.email)}
                        className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-all"
                        title="Delete subscriber"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Subscribers Count by Location */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          {locations.map(loc => {
            const count = subscribers.filter(s => `${s.city}, ${s.country}` === loc).length;
            return (
              <div key={loc} className="bg-white/10 backdrop-blur-md rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-5 h-5 text-white" />
                  <span className="text-white font-semibold">{loc}</span>
                </div>
                <div className="text-3xl font-bold text-white">{count}</div>
                <div className="text-white/70 text-sm">subscribers</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Email Templates Modal */}
      {showEmailPanel && (
        <EmailTemplates
          selectedSubscribers={Array.from(selectedSubscribers).map(id =>
            subscribers.find(s => s.id === id)!
          )}
          onClose={() => {
            setShowEmailPanel(false);
            setSelectedSubscribers(new Set());
          }}
          onSent={() => {
            setShowEmailPanel(false);
            setSelectedSubscribers(new Set());
          }}
        />
      )}
    </div>
  );
}
